// Publish from the page where the menu is edited, and say what is happening:
// sending, publishing (the worker builds the page, usually 10 to 20 seconds),
// then "Live since" with the time. The same panel shows the live time on the
// menus list. Slice 94 (owner asked: publish where I edit, and tell me when).

import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import type { MenuStatus, PublishResponse } from '../../api/types.js';

// The status endpoint is one small row; polling keeps "Publishing…" honest.
export const MENU_STATUS_POLL_MS = 3000;
// After this long without the pointer moving, say so instead of spinning for ever.
export const PUBLISH_SLOW_AFTER_MS = 90_000;

export function formatPublishedAt(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  const sameDay = at.toDateString() === now.toDateString();
  if (sameDay) return `today at ${time}`;
  const date = at.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${date} at ${time}`;
}

export function minutesAgo(iso: string, now: Date = new Date()): string {
  const mins = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function LiveSince(props: { readonly status: MenuStatus | undefined }): JSX.Element | null {
  const current = props.status?.current;
  if (!current) return null;
  return (
    <span>
      Live: v{current.menuVersion}, published {formatPublishedAt(current.publishedAt)} (
      {minutesAgo(current.publishedAt)})
    </span>
  );
}

function publishError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'could not publish';
  if (err.status === 403) return 'You do not have permission to publish.';
  if (err.status === 409) return 'Someone else edited this first. Reload the page and try again.';
  if (err.status === 0) return 'Network error. Check your connection.';
  return err.message || 'could not publish';
}

export function PublishPanel(props: {
  readonly orgId: string;
  readonly outletId: string;
  readonly menuId: string;
  readonly menuVersion: number;
  readonly canPublish: boolean;
  /** Re-read the menu after a publish (its version moves). */
  readonly detailKey: readonly unknown[];
  /** Tests only: how long to wait before saying it is slow. */
  readonly slowAfterMs?: number;
}): JSX.Element | null {
  const { orgId, outletId, menuId, menuVersion, canPublish, detailKey } = props;
  const slowAfterMs = props.slowAfterMs ?? PUBLISH_SLOW_AFTER_MS;
  const qc = useQueryClient();
  const statusKey = ['menu-status', orgId, outletId] as const;

  const status = useQuery({
    queryKey: statusKey,
    enabled: outletId !== '',
    queryFn: () => apiFetch<MenuStatus>(`/organizations/${orgId}/outlets/${outletId}/menu-status`),
    refetchInterval: MENU_STATUS_POLL_MS,
    refetchIntervalInBackground: false,
  });

  // The publish we sent and are waiting to see go live.
  const [waiting, setWaiting] = useState<{ publishId: string; startedAt: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [justWentLive, setJustWentLive] = useState<string | null>(null);

  const publish = useMutation({
    mutationFn: () =>
      apiFetch<PublishResponse>(`/organizations/${orgId}/menus/${menuId}/publish`, {
        method: 'POST',
        body: { expectedVersion: menuVersion },
      }),
    onSuccess: (res) => {
      setJustWentLive(null);
      setWaiting({ publishId: res.publishId, startedAt: Date.now() });
      void qc.invalidateQueries({ queryKey: detailKey });
      void qc.invalidateQueries({ queryKey: ['menus', orgId, outletId] });
      void qc.invalidateQueries({ queryKey: statusKey });
    },
  });

  const livePublishId = status.data?.current?.publishId ?? null;
  useEffect(() => {
    if (waiting && livePublishId === waiting.publishId) {
      setJustWentLive(waiting.publishId);
      setWaiting(null);
    }
  }, [waiting, livePublishId]);

  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waiting]);

  const slow = waiting !== null && now - waiting.startedAt > slowAfterMs;

  return (
    <section className="card publish-panel" aria-label="Publish this menu">
      <div className="row">
        {canPublish ? (
          <button
            type="button"
            disabled={publish.isPending || waiting !== null}
            onClick={() => publish.mutate()}
          >
            {publish.isPending ? 'Sending…' : waiting ? 'Publishing…' : 'Publish'}
          </button>
        ) : null}
        <p className="publish-status" role="status">
          {waiting ? (
            slow ? (
              'Still publishing. This is taking longer than usual; check again in a minute.'
            ) : (
              'Publishing. The menu page is being built, usually 10 to 20 seconds.'
            )
          ) : justWentLive ? (
            <>
              <strong>Published.</strong> <LiveSince status={status.data} />
            </>
          ) : status.data?.current ? (
            <LiveSince status={status.data} />
          ) : status.data ? (
            'Nothing has been published yet.'
          ) : (
            ' '
          )}
        </p>
      </div>
      {publish.error ? <p className="error">{publishError(publish.error)}</p> : null}
      {!canPublish ? <p className="muted">You can view this menu but not publish it.</p> : null}
    </section>
  );
}
